import { useFormContext } from 'react-hook-form';
import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import { ErrorResponse } from '../../../../types/API';
import { useFlowsDispatch } from '../../../common/src/utils/globalVariables';
import { getUseFlowsSessionQKey } from '../api/getFlowsSessionById';
import { sendFlowAnswerByFlowId } from '../services/apiService';
import {
  checkQuestionnaire,
  prefillAnswer,
  setApiStatus,
  setValidationErrorMessage,
} from '../store/redux/slices/flowsSlice';
import {
  FlowsAPIAnswers,
  FlowsAPIAnswerSuccess,
  FlowsAPIAnswerError,
} from '../types/ApiTypes';
import { FlowsSession } from '../types/FlowsSession';
import { isAPIErrorResponse, isValidFlowsResponse } from '../types/TypeGuards';
import useFlowsLocalAnswer from './useFlowsLocalAnswer';

interface SubmitFlowAnswersParams {
  flowId: string;
  sessionId: string;
  answers: FlowsAPIAnswers;
  saveProgress?: boolean;
}

/**
 * Submit the answers to the flows backend. It makes sure the Flows redux state
 * is up to date for the response.
 */
export const useSubmitFlowAnswers = (): UseMutationResult<
  FlowsAPIAnswerSuccess | FlowsAPIAnswerError,
  FlowsAPIAnswerError | ErrorResponse,
  SubmitFlowAnswersParams
> => {
  const flowsDispatch = useFlowsDispatch();
  const { setError } = useFormContext() || {};
  const { fillAnswers } = useFlowsLocalAnswer();
  const queryClient = useQueryClient();

  const submitFlowAnswers = async ({
    flowId,
    sessionId,
    answers,
    saveProgress = false,
  }: SubmitFlowAnswersParams) => {
    if (saveProgress) flowsDispatch(setApiStatus('LOADING'));

    const [resp, status] = await sendFlowAnswerByFlowId(
      flowId,
      sessionId,
      answers,
    );

    if (status !== 200 || !isValidFlowsResponse(resp)) {
      return Promise.reject(resp);
    }

    return resp;
  };

  return useMutation<
    FlowsAPIAnswerSuccess | FlowsAPIAnswerError,
    FlowsAPIAnswerError | ErrorResponse,
    SubmitFlowAnswersParams
  >(submitFlowAnswers, {
    onError: (error, { saveProgress }) => {
      // Check if API gives an error or Flows gives a structured response

      if (isAPIErrorResponse(error)) {
        // Only if it is a server error the answer is not saved
        // In other cases the answer is always persisted by BE
        if (error?.code && error?.code >= 500)
          flowsDispatch(setApiStatus('ERROR'));
        else flowsDispatch(setApiStatus('SUCCESS'));
        if (saveProgress) return;

        // Regex error handling
        const regex = /Problem with keys \['(.*)'\]: string does.*/g;
        const result = regex.exec(error.detail as string);
        if (result && result[1]) {
          // Will return value like "address', 'street"
          // - Replace the '
          // - Trim the string
          // - Split and take the first part
          const errorName = result[1].replace("'", '').trim().split(',')[0];
          setError(errorName, {
            type: 'manual',
            message: 'Only alphanumeric characters are allowed',
          });
        } else {
          flowsDispatch(setValidationErrorMessage('API Error'));
        }
      } else {
        flowsDispatch(setApiStatus('SUCCESS'));
        if (saveProgress) return;
        error.errors.forEach(({ message, target }) => {
          setError(target, {
            type: 'manual',
            message: message,
          });
        });
      }
    },
    onSuccess: (data, { sessionId }) => {
      flowsDispatch(setApiStatus('SUCCESS'));

      const response = data as FlowsAPIAnswerSuccess;

      // Fill in the cascading answers
      const cascadedAnswers = response.answers;
      if (cascadedAnswers?.length) {
        fillAnswers(
          cascadedAnswers.map((answer) => ({
            ...answer,
            groupId: answer.group_id || '',
          })),
        );
      }

      const flowsSesssionQKey = getUseFlowsSessionQKey(sessionId);

      const flowsSession =
        queryClient.getQueryData<FlowsSession>(flowsSesssionQKey);

      if (flowsSession) {
        // Add to the new entities to the cache
        const createdEntityReferences = response.entities?.created;
        if (createdEntityReferences?.length) {
          queryClient.setQueryData<FlowsSession>(flowsSesssionQKey, {
            ...flowsSession,
            entity_references: [
              ...(flowsSession?.entity_references || []),
              ...createdEntityReferences,
            ],
          });
        }

        // Update existing entities in the cache
        const updatedEntityReferences = response.entities?.updated;
        if (updatedEntityReferences?.length) {
          queryClient.setQueryData<FlowsSession>(flowsSesssionQKey, {
            ...flowsSession,
            entity_references: flowsSession?.entity_references?.map(
              (entity) => {
                const updatedEntity = updatedEntityReferences.find(
                  (updatedEntity) => updatedEntity.id === entity.id,
                );
                return updatedEntity || entity;
              },
            ),
          });
        }
      }

      // Fill in the default answers
      const defaultAnswers = response.default_values;
      if (defaultAnswers?.length) {
        defaultAnswers.forEach(({ id, group_id, answer }) =>
          flowsDispatch(
            prefillAnswer({
              id,
              groupId: group_id,
              answer,
              onlyInitialQuestionnaire: true,
            }),
          ),
        );
      }

      // IF valid response do checkQuestionnaire
      flowsDispatch(checkQuestionnaire());
    },
  });
};
